import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Package, Plus } from "lucide-react";
import { PoStatusBadge, money } from "./poUtils";
import { ProcurementListDialog } from "./ProcurementListDialog";
import { PoDetailDialog } from "./PoDetailDialog";
import { ReceiveDialog } from "./ReceiveDialog";

// Embedded list for a customer or project; handles create/view/edit/receive in place.
export const ProcurementListsCard = ({ projectId, customerId }) => {
  const [lists, setLists] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [receiving, setReceiving] = useState(null);

  const load = useCallback(() => {
    const q = projectId ? `project_id=${projectId}` : `customer_id=${customerId}`;
    axios.get(`${API}/procurement/lists?${q}`).then(r => setLists(r.data || [])).catch(() => setLists([]));
  }, [projectId, customerId]);
  useEffect(() => { load(); }, [load]);

  const open = lists.filter(l => !["received", "cancelled"].includes(l.status));

  return (
    <Card data-testid="procurement-lists-card">
      <CardHeader>
        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-base flex items-center gap-2"><Package className="w-5 h-5 text-indigo-600" /> Material Procurement ({open.length} open)</CardTitle>
          <Button size="sm" variant="outline" className="h-auto w-full whitespace-normal sm:w-auto" onClick={() => setShowNew(true)} data-testid="create-procurement-list-btn"><Plus className="w-3 h-3 mr-1" /> Create Material Procurement List</Button>
        </div>
      </CardHeader>
      <CardContent>
        {lists.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-3">No material lists yet. Create one to order from a supplier{projectId ? " — it will be tagged to this project automatically" : ""}.</p>
        ) : (
          <div className="space-y-2">
            {lists.map(po => (
              <button key={po.po_id} onClick={() => setViewing(po)} data-testid={`po-row-${po.po_id}`}
                className="w-full text-left flex items-center justify-between gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">{po.po_number} · {po.supplier_name}{po.title ? ` — ${po.title}` : ""}</p>
                  <p className="text-xs text-slate-500">{po.items.length} item(s) · {money(po.total)}{po.expected_date ? ` · ETA ${po.expected_date}` : ""}{!projectId && po.project_name ? ` · ${po.project_name}` : ""}</p>
                </div>
                <PoStatusBadge status={po.status} overdue={po.is_overdue} />
              </button>
            ))}
          </div>
        )}
      </CardContent>

      <ProcurementListDialog open={showNew || !!editing} onOpenChange={(v) => { if (!v) { setShowNew(false); setEditing(null); } }} po={editing} projectId={projectId} customerId={customerId} onSaved={load} />
      <PoDetailDialog po={viewing} open={!!viewing} onOpenChange={(v) => { if (!v) setViewing(null); }} onChanged={load} onEdit={setEditing} onReceive={setReceiving} />
      <ReceiveDialog po={receiving} open={!!receiving} onOpenChange={(v) => { if (!v) setReceiving(null); }} onDone={load} />
    </Card>
  );
};
